import React, { useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import BarChartIcon from '@mui/icons-material/BarChart';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CloseIcon from '@mui/icons-material/Close';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import BuildIcon from '@mui/icons-material/Build';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { toDisplayName, getDateDimensions } from '../../utils';
import { ROW_LIMIT_OPTIONS } from '../../constants';
import LoadSaved from '../LoadSaved';
import { qbIconButton, qbIconButtonOn, qbTitle, qbTopBar, qbTopBarToolbar } from '../VoyagerPanes/queryBuilderPresentation';

const countChipSx = (active: boolean) => ({
  borderRadius: '999px',
  border: 0,
  boxShadow: 'none',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0,
  color: active ? 'var(--agent)' : 'var(--ink-3)',
  bgcolor: active ? 'var(--agent-tint)' : 'var(--dim-tint)',
  '& .MuiChip-icon': { color: 'inherit', fontSize: 15 },
});

const TopBarControl: React.FC = () => {
  const domains = useVoyagerStore((s) => s.domains);
  const perspectives = useVoyagerStore((s) => s.perspectives);
  const selectedDomain = useVoyagerStore((s) => s.selectedDomain);
  const selectedPerspective = useVoyagerStore((s) => s.selectedPerspective);
  const selectedMetrics = useVoyagerStore((s) => s.selectedMetrics);
  const selectedDimensions = useVoyagerStore((s) => s.selectedDimensions);
  const error = useVoyagerStore((s) => s.error);
  const fetchDomains = useVoyagerStore((s) => s.fetchDomains);
  const selectDomain = useVoyagerStore((s) => s.selectDomain);
  const selectPerspective = useVoyagerStore((s) => s.selectPerspective);
  const execQuery = useVoyagerStore((s) => s.executeQuery);
  const clearSelections = useVoyagerStore((s) => s.clearSelections);
  const isLoading = useVoyagerStore((s) => s.isLoading);
  const navigateToLanding = useVoyagerStore((s) => s.navigateToLanding);
  const openedFromAnswer = useVoyagerStore((s) => !!s.queryOrigin);
  const backToConversation = useVoyagerStore((s) => s.backToConversation);
  const navigateToStudioWithContext = useVoyagerStore((s) => s.navigateToStudioWithContext);
  const userSettings = useVoyagerStore((s) => s.userSettings);
  const updateUserSettings = useVoyagerStore((s) => s.updateUserSettings);
  const setModelSource = useVoyagerStore((s) => s.setModelSource);
  const fetchAvailableDrafts = useVoyagerStore((s) => s.fetchAvailableDrafts);
  const availableDrafts = useVoyagerStore((s) => s.availableDrafts);
  const activeDraftId = useVoyagerStore((s) => s.activeDraftId);
  const activeDraftName = useVoyagerStore((s) => s.activeDraftName);
  const semanticModel = useVoyagerStore((s) => s.semanticModel);
  const activeDateField = useVoyagerStore((s) => s.activeDateField);
  const setActiveDateField = useVoyagerStore((s) => s.setActiveDateField);
  const workspaceAgentPanelOpen = useVoyagerStore((s) => s.workspaceAgentPanelOpen);
  const toggleWorkspaceAgentPanel = useVoyagerStore((s) => s.toggleWorkspaceAgentPanel);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [overflowAnchor, setOverflowAnchor] = React.useState<null | HTMLElement>(null);
  const overflowOpen = Boolean(overflowAnchor);
  const openOverflow = (e: React.MouseEvent<HTMLElement>) => setOverflowAnchor(e.currentTarget);
  const closeOverflow = () => setOverflowAnchor(null);
  const topBarDateDimensions = semanticModel ? getDateDimensions(semanticModel) : [];
  // Without the domain layer (#464) the one picker lists the whole catalog, by name.
  const domainsOn = useVoyagerStore((s) => s.features.domains);
  const perspectiveOptions = React.useMemo(
    () => (domainsOn ? perspectives : [...perspectives].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))),
    [perspectives, domainsOn],
  );

  const handleSettingsOpen = React.useCallback(() => {
    setSettingsOpen(true);
    if (semanticModel?.model) {
      fetchAvailableDrafts();
    }
  }, [semanticModel?.model, fetchAvailableDrafts]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  return (
    <AppBar position="static" elevation={0} color="default" sx={qbTopBar}>
      <Toolbar variant="dense" sx={qbTopBarToolbar}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          <Tooltip title="Back to home">
            <IconButton size="small" aria-label="Back to home" onClick={navigateToLanding} sx={qbIconButton}>
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Typography component="h1" sx={qbTitle}>Query Builder</Typography>
          {openedFromAnswer && (
            <ButtonBase onClick={backToConversation} data-testid="back-to-conversation"
              sx={{ ml: 1, gap: 0.5, fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', borderRadius: '6px', px: 0.75, py: 0.25,
                '&:hover': { color: 'var(--ink)', textDecoration: 'underline' } }}>
              <ArrowBackIcon sx={{ fontSize: 15 }} />Back to conversation
            </ButtonBase>
          )}
        </Box>

        {error && (domainsOn ? domains : perspectives).length === 0 && (
          <Chip label="Backend not connected" color="error" size="small" variant="outlined" sx={{ mr: 1 }} />
        )}

        {domainsOn && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: 13 }}>Domain</InputLabel>
            <Select
              value={selectedDomain || ''}
              label="Domain"
              onChange={(e) => selectDomain(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              {domains.map((d) => (
                <MenuItem key={d.id} value={d.id} sx={{ fontSize: 13 }}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ fontSize: 13 }}>Perspective</InputLabel>
          <Select
            value={selectedPerspective || ''}
            label="Perspective"
            onChange={(e) => selectPerspective(e.target.value)}
            disabled={domainsOn && !selectedDomain}
            sx={{ fontSize: 13 }}
          >
            {perspectiveOptions.map((dm) => (
              <MenuItem key={dm.id} value={dm.id} sx={{ fontSize: 13 }}>
                {dm.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {topBarDateDimensions.length >= 1 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: 13 }}>Date Context</InputLabel>
            <Select
              value={activeDateField || ''}
              label="Date Context"
              onChange={(e) => setActiveDateField(e.target.value || null)}
              sx={{ fontSize: 13 }}
            >
              {topBarDateDimensions.map((dd) => (
                <MenuItem key={dd.name} value={dd.name} sx={{ fontSize: 13 }}>
                  {toDisplayName(dd.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 0 }}>
          <Chip
            icon={<BarChartIcon />}
            label={`${selectedMetrics.length} metrics`}
            size="small"
            color={selectedMetrics.length > 0 ? 'primary' : 'default'}
            variant="outlined"
            sx={countChipSx(selectedMetrics.length > 0)}
          />
          <Chip
            icon={<ViewColumnIcon />}
            label={`${selectedDimensions.length} dimensions`}
            size="small"
            color={selectedDimensions.length > 0 ? 'primary' : 'default'}
            variant="outlined"
            sx={countChipSx(selectedDimensions.length > 0)}
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        <Tooltip title={workspaceAgentPanelOpen ? 'Hide Agent' : 'Ask Agent'}>
          <IconButton
            size="small"
            onClick={toggleWorkspaceAgentPanel}
            color={workspaceAgentPanelOpen ? 'primary' : 'default'}
            aria-label={workspaceAgentPanelOpen ? 'Hide Agent' : 'Ask Agent'}
            aria-pressed={workspaceAgentPanelOpen}
            sx={workspaceAgentPanelOpen ? qbIconButtonOn : qbIconButton}
          >
            <AutoAwesomeIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Execute Query (⌘⇧↵)">
          <span>
            <Button
              variant="run"
              color="success"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={execQuery}
              disabled={isLoading}
            >
              Run
            </Button>
          </span>
        </Tooltip>

        <LoadSaved>
          {({ openSave, openLoad }) => (
            <>
              <Tooltip title="More actions">
                <IconButton size="small" onClick={openOverflow} aria-label="More actions" sx={qbIconButton}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={overflowAnchor}
                open={overflowOpen}
                onClose={closeOverflow}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => { closeOverflow(); clearSelections(); }}
                  sx={{ fontSize: 13 }}
                >
                  <ListItemIcon><DeleteSweepIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Clear selections</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => { closeOverflow(); openSave(); }}
                  disabled={!selectedPerspective || selectedMetrics.length === 0}
                  sx={{ fontSize: 13 }}
                >
                  <ListItemIcon><SaveIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Save report</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => { closeOverflow(); openLoad(); }}
                  sx={{ fontSize: 13 }}
                >
                  <ListItemIcon><FolderOpenIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Load report</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => { closeOverflow(); navigateToStudioWithContext(); }}
                  disabled={!selectedPerspective}
                  sx={{ fontSize: 13 }}
                >
                  <ListItemIcon><BuildIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Edit in Studio</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => { closeOverflow(); handleSettingsOpen(); }}
                  sx={{ fontSize: 13 }}
                >
                  <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Settings</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </LoadSaved>
      </Toolbar>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Settings
          <IconButton size="small" onClick={() => setSettingsOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Result Row Limit
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={userSettings.resultLimit}
                onChange={(e) => updateUserSettings({ resultLimit: Number(e.target.value) })}
              >
                {ROW_LIMIT_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n.toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Maximum number of rows returned by generated SQL queries.
            </Typography>
          </Box>

          {semanticModel && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Model Source
                </Typography>
                <RadioGroup
                  value={userSettings.modelSource}
                  onChange={(e) => {
                    const val = e.target.value as 'production' | 'draft';
                    if (val === 'production') {
                      setModelSource('production');
                    }
                    if (val === 'draft') {
                      updateUserSettings({ modelSource: 'draft' });
                    }
                  }}
                >
                  <FormControlLabel
                    value="production"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Production (default)</Typography>}
                  />
                  <FormControlLabel
                    value="draft"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">Draft</Typography>}
                  />
                </RadioGroup>

                {userSettings.modelSource === 'draft' && (
                  <Box sx={{ mt: 1 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Select draft</InputLabel>
                      <Select
                        value={activeDraftId || ''}
                        label="Select draft"
                        onChange={(e) => {
                          const draftId = e.target.value as string;
                          const draft = availableDrafts.find((d) => d.id === draftId);
                          if (draft) {
                            setModelSource('draft', draft.id, draft.name);
                          }
                        }}
                      >
                        {availableDrafts.length === 0 ? (
                          <MenuItem disabled value="">
                            No drafts available for this model
                          </MenuItem>
                        ) : (
                          availableDrafts.map((d) => (
                            <MenuItem key={d.id} value={d.id}>
                              {d.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    {activeDraftId && activeDraftName && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Active draft: {activeDraftName}
                      </Typography>
                    )}
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Preview unpublished draft models in the Workspace.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} size="small">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default TopBarControl;
